import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAbsencePolicyStore } from '../../../store/useAbsencePolicyStore';
import { Card } from '../../../components/Shared/Card';
import { Button } from '../../../components/Shared/Button';
import { Loading } from '../../../components/Shared/Loading';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { FormField } from '../../../components/Shared/FormField';
import type { AbsencePolicyDto, CarryOverMode, VacationYearMode } from '../../../types';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MAX_DAYS_PER_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const selectClass =
  'w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';

export default function AbsenceSettingsPage() {
  const { t } = useTranslation(['absenceSettings', 'common']);
  const { policy, loading, saving, error, fetchPolicy, updatePolicy } = useAbsencePolicyStore();

  const [form, setForm] = useState<AbsencePolicyDto | null>(null);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  useEffect(() => {
    if (policy) setForm(policy);
  }, [policy]);

  const isDirty = useMemo(
    () => Boolean(form && policy && JSON.stringify(form) !== JSON.stringify(policy)),
    [form, policy],
  );

  if (loading && !form) return <Loading />;

  if (error && !form) {
    return (
      <Card>
        <EmptyState
          variant="error"
          title={t('absenceSettings:errors.fetchFailed')}
          onRetry={fetchPolicy}
        />
      </Card>
    );
  }

  if (!form) return null;

  const patch = (changes: Partial<AbsencePolicyDto>) => setForm({ ...form, ...changes });

  // why: die Tagesauswahl folgt dem Monat, damit kein 31. Februar gewählt werden kann —
  // das Backend lehnt solche Kombinationen ohnehin ab.
  const daysIn = (month: number) => MAX_DAYS_PER_MONTH[Math.min(Math.max(month, 1), 12) - 1];
  const dayOptions = (month: number) =>
    Array.from({ length: daysIn(month) }, (_, index) => index + 1);

  const handleSave = async () => {
    try {
      await updatePolicy(form);
    } catch {
      // Fehlermeldung kommt aus dem Store-Toast.
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('absenceSettings:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('absenceSettings:subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setForm(policy)}
              disabled={!isDirty || saving}
            >
              {t('absenceSettings:buttons.reset')}
            </Button>
            <Button onClick={handleSave} disabled={!isDirty || saving}>
              {t('absenceSettings:buttons.save')}
            </Button>
          </div>
        </div>
      </div>

      <Card title={t('absenceSettings:sections.vacationYear')}>
        <div className="space-y-4">
          <FormField label={t('absenceSettings:fields.vacationYearMode')}>
            <select
              className={selectClass}
              value={form.vacationYearMode}
              onChange={(e) => patch({ vacationYearMode: e.target.value as VacationYearMode })}
              aria-label={t('absenceSettings:fields.vacationYearMode')}
            >
              <option value="CalendarYear">{t('absenceSettings:options.calendarYear')}</option>
              <option value="EmploymentYear">{t('absenceSettings:options.employmentYear')}</option>
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              {t('absenceSettings:hints.vacationYearMode')}
            </p>
          </FormField>

          {form.vacationYearMode === 'CalendarYear' && (
            <FormField label={t('absenceSettings:fields.vacationYearStart')}>
              <div className="grid grid-cols-2 gap-3">
                <select
                  className={selectClass}
                  value={form.vacationYearStartMonth}
                  onChange={(e) => {
                    const month = Number(e.target.value);
                    patch({
                      vacationYearStartMonth: month,
                      vacationYearStartDay: Math.min(form.vacationYearStartDay, daysIn(month)),
                    });
                  }}
                  aria-label={t('absenceSettings:fields.vacationYearStart')}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {t(`absenceSettings:months.${m}`)}
                    </option>
                  ))}
                </select>
                <select
                  className={`${selectClass} tabular-nums`}
                  value={form.vacationYearStartDay}
                  onChange={(e) => patch({ vacationYearStartDay: Number(e.target.value) })}
                  aria-label={t('absenceSettings:fields.vacationYearStart')}
                >
                  {dayOptions(form.vacationYearStartMonth).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>
          )}
        </div>
      </Card>

      <Card title={t('absenceSettings:sections.entitlement')}>
        <FormField
          label={t('absenceSettings:fields.defaultAnnualVacationDays')}
          type="number"
          min={0}
          max={365}
          step={0.5}
          value={String(form.defaultAnnualVacationDays)}
          onChange={(e) => patch({ defaultAnnualVacationDays: Number(e.target.value) })}
          className="tabular-nums"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          {t('absenceSettings:hints.defaultAnnualVacationDays')}
        </p>
        <div className="mt-4">
          <FormField label={t('absenceSettings:fields.roundVacationDays')}>
            <label className="flex items-center gap-2 mt-1 text-[13px] text-slate-700">
              <input
                type="checkbox"
                checked={form.roundVacationDaysToWholeDays}
                onChange={(e) => patch({ roundVacationDaysToWholeDays: e.target.checked })}
                className="rounded border-slate-300"
              />
              {t('absenceSettings:hints.roundVacationDays')}
            </label>
          </FormField>
        </div>
      </Card>

      <Card title={t('absenceSettings:sections.carryOver')}>
        <div className="space-y-4">
          <FormField label={t('absenceSettings:fields.carryOverMode')}>
            <select
              className={selectClass}
              value={form.carryOverMode}
              onChange={(e) => patch({ carryOverMode: e.target.value as CarryOverMode })}
              aria-label={t('absenceSettings:fields.carryOverMode')}
            >
              <option value="None">{t('absenceSettings:options.carryOverNone')}</option>
              <option value="Unlimited">{t('absenceSettings:options.carryOverUnlimited')}</option>
              <option value="ExpiresAt">{t('absenceSettings:options.carryOverExpiresAt')}</option>
            </select>
          </FormField>

          {form.carryOverMode === 'ExpiresAt' && (
            <FormField label={t('absenceSettings:fields.carryOverExpiry')}>
              <div className="grid grid-cols-2 gap-3">
                <select
                  className={selectClass}
                  value={form.carryOverExpiryMonth}
                  onChange={(e) => {
                    const month = Number(e.target.value);
                    patch({
                      carryOverExpiryMonth: month,
                      carryOverExpiryDay: Math.min(form.carryOverExpiryDay, daysIn(month)),
                    });
                  }}
                  aria-label={t('absenceSettings:fields.carryOverExpiry')}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {t(`absenceSettings:months.${m}`)}
                    </option>
                  ))}
                </select>
                <select
                  className={`${selectClass} tabular-nums`}
                  value={form.carryOverExpiryDay}
                  onChange={(e) => patch({ carryOverExpiryDay: Number(e.target.value) })}
                  aria-label={t('absenceSettings:fields.carryOverExpiry')}
                >
                  {dayOptions(form.carryOverExpiryMonth).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </FormField>
          )}
        </div>
      </Card>

      <Card title={t('absenceSettings:sections.certificate')}>
        <FormField
          label={t('absenceSettings:fields.sickCertificateRequiredFromDays')}
          type="number"
          min={1}
          max={365}
          value={form.sickCertificateRequiredFromDays == null ? '' : String(form.sickCertificateRequiredFromDays)}
          onChange={(e) =>
            patch({
              sickCertificateRequiredFromDays: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className="tabular-nums"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          {t('absenceSettings:hints.certificateOff')}
        </p>
      </Card>

      <Card title={t('absenceSettings:sections.overtime')}>
        <FormField
          label={t('absenceSettings:fields.overtimeStartDate')}
          type="date"
          value={form.overtimeStartDate ?? ''}
          onChange={(e) => patch({ overtimeStartDate: e.target.value || null })}
          className="tabular-nums"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          {t('absenceSettings:hints.overtimeStartDate')}
        </p>
      </Card>
    </div>
  );
}
